import React, { useId } from "react"
import cx from "classnames"
import library from "./assets/icon-map"
import type { IconName } from "./assets/icon-map"
import { Tooltip } from "../tooltip"
import { VisuallyHidden } from "../visually-hidden"

const SIZES: Record<string, string> = {
  xs: "w-4 h-4",
  sm: "w-5 h-5",
  md: "w-6 h-6",
  lg: "w-8 h-8"
}

export type IconProps = {
  /** Matches name in Figma, using lowercase and hyphens instead of spaces. */
  name: IconName
  /** Icon size */
  size?: "xs" | "sm" | "md" | "lg"
  /** Accessible alt text */
  alt?: string
  /** Customize the icon. E.g. text-gray-700 */
  className?: string
  "aria-hidden"?: boolean | "true" | "false"
  "aria-label"?: string
  style?: React.CSSProperties
  onClick?: React.MouseEventHandler<SVGSVGElement>
  "data-testid"?: string
  "data-slot"?: string
  color?: string
}

const lookupIcon = (iconName: string) => library.find(i => i.name === iconName)

const UnknownIcon = ({ name }: { name: string }) => {
  const message = `The icon "${name}" you're trying to use doesn't exist. See available icons in the component library's icon tokens`
  const questionIcon = lookupIcon("question-circle")
  if (!questionIcon) return null
  const QuestionIcon = questionIcon.component
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <QuestionIcon className="text-red-600 animate-bounce" role="img" />
        }
      />
      <Tooltip.Content position="bottom">{message}</Tooltip.Content>
    </Tooltip>
  )
}

/**
 * Renders an SVG icon from the design system icon library.
 * Icons are referenced by name and support three sizes.
 */
const Icon = ({ name, className, size = "md", alt, ...props }: IconProps) => {
  className = cx(
    "fill-current inline-block overflow-visible",
    SIZES[size],
    className
  )
  const labelId = useId()

  if (name && typeof name == "string") {
    const icon = lookupIcon(name)
    if (!icon) {
      return <UnknownIcon name={name} />
    }

    const IconComponent = icon.component

    return (
      <>
        <IconComponent
          className={className}
          name={name}
          style={{ fontSize: "inherit" }}
          aria-labelledby={alt ? labelId : undefined}
          role="img"
          {...props}
        />
        {alt && <VisuallyHidden id={labelId}>{alt}</VisuallyHidden>}
      </>
    )
  }

  return <UnknownIcon name={String(name)} />
}

export type { IconName }
export { Icon }
export default Icon
